import * as React from 'react';
import { Table, Group, createStyles, getStylesRef } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useEnumDataSet } from '../Form/hooks/useEnumDataSet';
import { ROW_TYPES } from '../../types';

export interface IInfoTableRow {
    label?: string | React.ReactNode;
    value: any;
    index?: number;
    key?: string;
    isTotal?: boolean;
    type: ROW_TYPES;
    isFooterRow?: boolean;
    noHover?: boolean;
    isHeader?: boolean;
    hidden?: boolean;
    rightAction?: React.ReactNode;
}
interface InfoTableProps {
    rows: (IInfoTableRow | {})[];
    alternatingRows?: boolean;
    rowActions?: boolean;
    blueHeader?: boolean;
    totalHighlight?: boolean;
    rightAlignSecondCol?: boolean;
    hidden?: boolean;
    background?: string;
    tableHeader?: React.ReactNode;
    headerCells?: (React.ReactNode | string)[];
}

const useStyles = createStyles((theme) => ({
    firstCol: {
        width: '16rem',
        fontWeight: 500
    },
    secondCol: {
        width: '4rem'
    },
    header: {
        borderBottom: '1px solid #dee2e6',
        background: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background,
        textAlign: 'left',
        fontSize: '14px'
    },
    headerCell: {
        // paddingRight: '10px',
        padding: '4px',
        paddingLeft: '1.35rem !important'
    },
    actionsCol: {
        width: '3rem'
    },
    blankRow: {
        height: '36px',
        background: 'transparent !important'
    },
    alternatingRow: {
        background: '#f9fcff'
    },
    footerRow: {
        borderTop: '2px solid #494949',
        background: 'transparent !important'
    },
    noHover: {
        // background: 'transparent !important'
    },
    isHeader: {
        background: 'transparent !important'
    },
    isTotal: {
        position: 'relative',
        '&:after': {
            content: '""',
            position: 'absolute',
            top: '-1px',
            right: 0,
            left: 0,
            height: '2px',
            background: '#cacaca'
        }
    },
    spaceBeneath: {
        paddingBottom: '30px !important'
    },
    table: {
        borderCollapse: 'collapse'
    },
    blueHeader: {
        background: 'rgb(228 243 255)'
    },
    totalHighlight: {
        'td *': {
            color: '#1964ab !important',
            fontWeight: 700
        }
    }
}));

export const InfoTable: React.FC<InfoTableProps> = ({
    rows = [],
    alternatingRows,
    tableHeader,
    rightAlignSecondCol,
    headerCells,
    blueHeader,
    background,
    totalHighlight = false,
    rowActions = true
}) => {
    const { classes, cx } = useStyles();
    const { t: commonTranslations } = useTranslation('common');

    const transformedRows = React.useMemo(
        () =>
            rows
                .map((row, index) => {
                    if (!Object.keys(row).length) {
                        return (
                            <tr key={`blank-${index}`} className={classes.blankRow}>
                                <td className={classes.firstCol}></td>
                                <td
                                    className={cx('', {
                                        [classes.secondCol]: rightAlignSecondCol
                                    })}
                                ></td>
                                <td className={classes.actionsCol}></td>
                            </tr>
                        );
                    }
                    const infoRow = row as IInfoTableRow;
                    const { label, value } = infoRow;
                    const spaceBeneath = (rows[index + 1] as IInfoTableRow)?.type === ROW_TYPES.TOTAL;

                    return !infoRow.hidden ? (
                        <tr
                            key={infoRow.key ?? index}
                            className={cx('', {
                                [classes.alternatingRow]: !infoRow.isFooterRow && alternatingRows && index % 2,
                                [classes.footerRow]: infoRow.isFooterRow,
                                [classes.noHover]: infoRow.noHover,
                                [classes.isHeader]: infoRow.isHeader,
                                [classes.isTotal]: infoRow.type === ROW_TYPES.TOTAL
                            })}
                        >
                            <td
                                className={cx(classes.firstCol, {
                                    [classes.spaceBeneath]: spaceBeneath
                                })}
                            >
                                {label}
                            </td>
                            <td
                                className={cx('', {
                                    [classes.secondCol]: rightAlignSecondCol,
                                    [classes.spaceBeneath]: spaceBeneath
                                })}
                            >
                                {typeof value === 'boolean' ? commonTranslations(value ? 'yes' : 'no') : value}
                            </td>
                            {rowActions ? (
                                <td
                                    className={cx(classes.actionsCol, {
                                        [classes.spaceBeneath]: spaceBeneath
                                    })}
                                >
                                    <Group position="right">{infoRow.rightAction ?? null}</Group>
                                </td>
                            ) : null}
                        </tr>
                    ) : null;
                })
                .filter(Boolean),
        [rows, classes]
    );

    return (
        <Table highlightOnHover>
            {headerCells && headerCells.length ? (
                <thead>
                    <tr
                        className={cx(classes.header, {
                            [classes.blueHeader]: blueHeader
                        })}
                    >
                        {headerCells.map((cell, index) => (
                            <th key={index} className={classes.headerCell}>
                                {cell}
                            </th>
                        ))}
                    </tr>
                </thead>
            ) : null}
            <tbody
                className={cx('', {
                    [classes.totalHighlight]: totalHighlight
                })}
                style={background ? { background } : {}}
            >
                {transformedRows}
            </tbody>
        </Table>
    );
};
